import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, InputGroup, Alert, Spinner, Badge, Row, Col, FormControl} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPlus, faToggleOn, faToggleOff } from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import { useEntidadeContext } from '../../EntidadeContext';
import { useAlertModal } from '../../AlertModalContext';

const CuponsTab = ({ currentUserRole }) => {
    const { currentEntidade } = useEntidadeContext();
    const { showAlertModal } = useAlertModal();

    const [cupons, setCupons] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showModal, setShowModal] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null); // null for create, object for edit
    const [formData, setFormData] = useState({
        codigo: '',
        valor_desconto: '',
        tipo_desconto: 'valor_fixo', // 'fixo' ou 'porcentagem'
        usos_maximos: '',
        data_validade: '',
        ativo: true,
    });

    const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
    const [couponToDelete, setCouponToDelete] = useState(null);

    const canManageCupons = currentUserRole === 'Representante Legal' || currentUserRole === 'Gerente';

    const fetchCupons = useCallback(async () => {
        if (!currentEntidade?.id) return;
        setIsLoading(true);
        setError(null);
        try {
            const response = await axios.get(`/api/cupons?entidade_id=${currentEntidade.id}`);
            setCupons(response.data.cupons);
        } catch (err) {
            setError('Não foi possível carregar os cupons.');
            console.error("Erro ao buscar cupons:", err);
            showAlertModal('Erro', 'Não foi possível carregar os cupons.', 'danger');
        } finally {
            setIsLoading(false);
        }
    }, [currentEntidade, showAlertModal]);

    useEffect(() => {
        fetchCupons();
    }, [fetchCupons]);

    const handleShowModal = (coupon = null) => {
        setEditingCoupon(coupon);
        if (coupon) {
            setFormData({
                codigo: coupon.codigo,
                valor_desconto: coupon.valor_desconto,
                tipo_desconto: coupon.tipo_desconto,
                usos_maximos: coupon.usos_maximos === -1 ? '' : coupon.usos_maximos, // -1 significa ilimitado
                data_validade: coupon.data_validade ? coupon.data_validade.split('T')[0] : '',
                ativo: coupon.ativo,
            });
        } else {
            setFormData({
                codigo: '',
                valor_desconto: '',
                tipo_desconto: 'valor_fixo',
                usos_maximos: '',
                data_validade: '',
                ativo: true,
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingCoupon(null);
        setFormData({});
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleSaveCoupon = async () => {
        if (!canManageCupons) {
            showAlertModal('Acesso Negado', 'Você não tem permissão para gerenciar cupons.', 'danger');
            return;
        }
        if (!formData.codigo || !formData.valor_desconto || !formData.tipo_desconto) {
            showAlertModal('Validação', 'Código, Valor de Desconto e Tipo de Desconto são obrigatórios.', 'warning');
            return;
        }

        setIsSaving(true);
        try {
            const dataToSave = {
                ...formData,
                entidade_id: currentEntidade.id,
                usos_maximos: formData.usos_maximos === '' ? -1 : parseInt(formData.usos_maximos, 10),
                valor_desconto: parseFloat(formData.valor_desconto),
            };

            if (editingCoupon) {
                await axios.put(`/api/cupons/${editingCoupon.id}`, dataToSave);
                showAlertModal('Sucesso', 'Cupom atualizado com sucesso!', 'success');
            } else {
                await axios.post('/api/cupons', dataToSave);
                showAlertModal('Sucesso', 'Cupom criado com sucesso!', 'success');
            }
            fetchCupons();
            handleCloseModal();
        } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao salvar cupom.';
            showAlertModal('Erro', msg, 'danger');
            console.error("Erro ao salvar cupom:", err);
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteCoupon = (coupon) => {
        if (!canManageCupons) {
            showAlertModal('Acesso Negado', 'Você não tem permissão para gerenciar cupons.', 'danger');
            return;
        }
        setCouponToDelete(coupon);
        setShowConfirmDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!couponToDelete) return;
        setIsSaving(true); // Reutiliza o isSaving para o modal de delete
        try {
            await axios.delete(`/api/cupons/${couponToDelete.id}`);
            showAlertModal('Sucesso', 'Cupom excluído com sucesso!', 'success');
            fetchCupons();
            setShowConfirmDeleteModal(false);
        } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao excluir cupom.';
            showAlertModal('Erro', msg, 'danger');
            console.error("Erro ao excluir cupom:", err);
        } finally {
            setIsSaving(false);
            setCouponToDelete(null);
        }
    };

    const handleToggleStatus = async (coupon) => {
        if (!canManageCupons) {
            showAlertModal('Acesso Negado', 'Você não tem permissão para gerenciar cupons.', 'danger');
            return;
        }
        try {
            await axios.patch(`/api/cupons/${coupon.id}/toggle-status`);
            showAlertModal('Sucesso', `Cupom ${coupon.ativo ? 'inativado' : 'ativado'} com sucesso!`, 'success');
            fetchCupons();
        } catch (err) {
            const msg = err.response?.data?.message || 'Erro ao alterar status do cupom.';
            showAlertModal('Erro', msg, 'danger');
            console.error("Erro ao alterar status do cupom:", err);
        }
    };

    if (isLoading) {
        return <div className="text-center py-5"><Spinner animation="border" /> Carregando cupons...</div>;
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    return (
        <>
            <div className="d-flex justify-content-end mb-3">
                {canManageCupons && (
                    <Button variant="primary" onClick={() => handleShowModal()}>
                        <FontAwesomeIcon icon={faPlus} className="me-2" /> Novo Cupom
                    </Button>
                )}
            </div>

            {cupons.length === 0 ? (
                <Alert variant="info">Nenhum cupom cadastrado ainda.</Alert>
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Código</th>
                            <th>Desconto</th>
                            <th>Tipo</th>
                            <th>Usos Máximos</th>
                            <th>Usos Atuais</th>
                            <th>Validade</th>
                            <th>Status</th>
                            {canManageCupons && <th className="text-center">Ações</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {cupons.map(coupon => (
                            <tr key={coupon.id}>
                                <td>{coupon.codigo}</td>
                                <td>{coupon.tipo_desconto === 'porcentagem' ? `${coupon.valor}%` : `R$ ${parseFloat(coupon.valor).toFixed(2)}`}</td>
                                <td>{coupon.tipo_desconto === 'valor_fixo' ? 'Fixo' : 'Porcentagem'}</td>
                                <td>{coupon.usos_maximos === -1 ? 'Ilimitado' : coupon.limite_uso}</td>
                                <td>{coupon.usos_atuais}</td>
                                <td>{coupon.data_validade ? new Date(coupon.data_validade).toLocaleDateString() : 'Sem validade'}</td>
                                <td>
                                    <Badge bg={coupon.ativo ? 'success' : 'danger'}>
                                        {coupon.ativo ? 'Ativo' : 'Inativo'}
                                    </Badge>
                                </td>
                                {canManageCupons && (
                                    <td className="text-center">
                                        <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleShowModal(coupon)}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </Button>
                                        <Button variant={coupon.ativo ? 'outline-danger' : 'outline-success'} size="sm" className="me-2" onClick={() => handleToggleStatus(coupon)}>
                                            <FontAwesomeIcon icon={coupon.ativo ? faToggleOn : faToggleOff} />
                                        </Button>
                                        <Button variant="outline-danger" size="sm" onClick={() => handleDeleteCoupon(coupon)}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </Button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            {/* Modal de Criação/Edição de Cupom */}
            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingCoupon ? 'Editar Cupom' : 'Novo Cupom'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Código do Cupom</Form.Label>
                            <FormControl
                                name="codigo"
                                value={formData.codigo}
                                onChange={handleChange}
                                placeholder="EX: DESCONTO10"
                                required
                            />
                        </Form.Group>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Valor do Desconto</Form.Label>
                                    <InputGroup>
                                        <InputGroup.Text>{formData.tipo_desconto === 'valor_fixo' ? 'R$' : '%'}</InputGroup.Text>
                                        <FormControl
                                            type="number"
                                            name="valor_desconto"
                                            value={formData.valor_desconto}
                                            onChange={handleChange}
                                            step="0.01"
                                            min="0"
                                            required
                                        />
                                    </InputGroup>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Tipo de Desconto</Form.Label>
                                    <Form.Select name="tipo_desconto" value={formData.tipo_desconto} onChange={handleChange} required>
                                        <option value="valor_fixo">Valor Fixo (R$)</option>
                                        <option value="porcentagem">Porcentagem (%)</option>
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Usos Máximos (Deixe em branco para ilimitado)</Form.Label>
                                    <FormControl
                                        type="number"
                                        name="usos_maximos"
                                        value={formData.usos_maximos}
                                        onChange={handleChange}
                                        min="-1" // Permite -1 para ilimitado, ou 0 para nenhum uso
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Data de Validade (Opcional)</Form.Label>
                                    <FormControl
                                        type="date"
                                        name="data_validade"
                                        value={formData.data_validade}
                                        onChange={handleChange}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="switch"
                                id="ativo-switch"
                                name="ativo"
                                label="Ativo"
                                checked={formData.ativo}
                                onChange={handleChange}
                            />
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseModal} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button variant="primary" onClick={handleSaveCoupon} disabled={isSaving}>
                        {isSaving ? <Spinner as="span" animation="border" size="sm" className="me-2" /> : null}
                        {isSaving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal de Confirmação de Exclusão */}
            <Modal show={showConfirmDeleteModal} onHide={() => setShowConfirmDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Exclusão</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Tem certeza que deseja excluir o cupom <strong>{couponToDelete?.codigo}</strong>?
                    Esta ação não pode ser desfeita.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowConfirmDeleteModal(false)} disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button variant="danger" onClick={handleConfirmDelete} disabled={isSaving}>
                        {isSaving ? <Spinner as="span" animation="border" size="sm" className="me-2" /> : null}
                        {isSaving ? 'Excluindo...' : 'Excluir'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default CuponsTab;
